/**
 * The building blocks both to-do lists share (FDE view and sales view): how a
 * to-do is recorded, how the list is sorted, and the checks that matter to
 * everyone on a pilot.
 */

import { formatDate, isPilotOverdue } from "./selectors";

const DAY_MS = 24 * 60 * 60 * 1000;
const STALE_ACTIVITY_DAYS = 7;
const PRIORITY_RANK = { high: 0, normal: 1, low: 2 };

/**
 * A collector that records to-dos for one pilot at a time.
 *
 * @returns {{ add: Function, list: Function }}
 */
export function createTodoList() {
  const todos = [];

  return {
    add(pilot, kind, priority, text) {
      todos.push({
        id: `${pilot.dealId}:${kind}`,
        dealId: pilot.dealId,
        pilotName: pilot.name,
        priority,
        text,
      });
    },
    list() {
      return [...todos].sort(
        (a, b) =>
          PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] ||
          a.pilotName.localeCompare(b.pilotName),
      );
    },
  };
}

/**
 * Checks every pilot gets, whoever is looking at it. `isDealOwner` words the
 * close-date item for the person who can change it themselves.
 */
export function addSharedChecks(todos, pilot, now, { isDealOwner = false } = {}) {
  const updateWho = isDealOwner
    ? "Update it in HubSpot."
    : `Update it with ${pilot.owner || "the deal owner"}.`;

  if (isPilotOverdue(pilot)) {
    todos.add(
      pilot,
      "overdue",
      "high",
      `Past the 3-week playbook (${pilot.daysInPilot} days). Agree the next step or close it out.`,
    );
  }
  if (pilot.closeDate && pilot.closeDate < now) {
    todos.add(
      pilot,
      "closeDate",
      "high",
      `Expected close date (${formatDate(pilot.closeDate)}) has passed. ${updateWho}`,
    );
  }
  if (pilot.recentUsage && pilot.recentUsage.activeUsers === 0) {
    todos.add(
      pilot,
      "noUsage",
      "high",
      "Nobody at the client used Addy in the last 7 days. Check in with the pilot users.",
    );
  }
  if (pilot.nextStep) {
    todos.add(pilot, "nextStep", "normal", `HubSpot next step: ${pilot.nextStep}`);
  }
  if (pilot.lastActivityAt) {
    const idleDays = Math.floor((now - pilot.lastActivityAt) / DAY_MS);
    if (idleDays > STALE_ACTIVITY_DAYS) {
      todos.add(
        pilot,
        "stale",
        "normal",
        `No HubSpot activity for ${idleDays} days. Log the latest call or note.`,
      );
    }
  }
}

/** The playbook action for the pilot's current step, or null when there is none. */
export function addPlaybookAction(todos, pilot, actionsBySubStage) {
  if (!pilot.subStage) {
    todos.add(
      pilot,
      "playbook",
      "normal",
      "Set the pilot's playbook step in HubSpot so everyone can see where it is.",
    );

    return;
  }
  const action = actionsBySubStage[pilot.subStage.id];
  if (action) {
    todos.add(pilot, "playbook", "normal", action);
  }
}
